import type { Readable } from 'svelte/motion';
import { derived, get, writable } from 'svelte/store';
import { localStorageService } from '$lib/services';
import { experimentService } from '$lib/services/experiment';

import { getStoryFeed } from '@api/story/story-feed';
import type { StoryFeedItem } from '@api/story/types';
import { isCurrentSubscriptionActive } from '@components/payment/store';
import {
    FEED_BLOCKING_TIME,
    LIMIT_PER_PAGE,
    LIMIT_TO_WATCH_AUTHORIZED,
    LIMIT_TO_WATCH_NON_AUTHORIZED,
} from '@components/story/feed/constants';
import { userStore } from '@components/user/store';
import { DAY_IN_MS } from '@shared/constants';

import type { Store, StoreState } from './types';

import { browser } from '$app/environment';

const initialValues: StoreState = {
    stories: [],
    storiesCount: 0,
    isMute: true,
    lastWatchedId: null,
    hideSwipeInfo: false,
    watchedStories: {},
    blockedDate: null,
    firstBlockedDate: null,
    blockingCount: 1,
};

function createStore(): Store {
    const store = writable(initialValues);
    const { subscribe, update, set } = store;

    const updateStore = (data: Partial<StoreState>) => {
        update((state) => ({ ...state, ...data }));
    };

    if (browser) {
        const storageBlockedDate = localStorageService.getItem('storyFeedBlockedDate');
        const blockedDate = storageBlockedDate ? Number(storageBlockedDate) : null;
        const storageFirstBlockingDate = localStorageService.getItem('storyFeedFirstBlockedDate');
        let firstBlockedDate = storageFirstBlockingDate ? Number(storageFirstBlockingDate) : null;
        const storageBlockingCount = localStorageService.getItem('storyFeedBlockingCount');
        let blockingCount = storageBlockingCount ? Number(storageBlockingCount) : 1;

        if (firstBlockedDate && Date.now() - firstBlockedDate > DAY_IN_MS) {
            firstBlockedDate = null;
            localStorageService.removeItem('storyFeedFirstBlockedDate');
            blockingCount = 1;
            localStorageService.removeItem('storyFeedBlockingCount');
        }

        updateStore({
            isMute: Boolean(localStorageService.getItem('storyFeedIsMute')),
            lastWatchedId: localStorageService.getItem('storyFeedLastWatchedId'),
            hideSwipeInfo: Boolean(localStorageService.getItem('storyFeedHideSwipeInfo')),
            blockedDate,
            blockingCount,
            firstBlockedDate,
        });
    }

    const loadStories = async (isUserExist: boolean, firstStoryId?: string) => {
        const { stories } = get(store);
        const data = await getStoryFeed({
            page: 1,
            itemsPerPage: stories.length || LIMIT_PER_PAGE,
            firstStoryId,
        });
        if (data) {
            updateStore({
                stories: data.items,
                storiesCount: data.count,
            });
            if (data.experiment) {
                experimentService.mergeCookiesAndConfig({
                    isUserExist,
                    experiments: [data.experiment],
                });
            }
        }
    };

    const addStories = async (isUserExist: boolean, page: number) => {
        const data = await getStoryFeed({ page, itemsPerPage: LIMIT_PER_PAGE });
        if (data) {
            update((state) => ({
                ...state,
                stories: [...state.stories, ...data.items],
                storiesCount: data.count,
            }));
            if (data.experiment) {
                experimentService.mergeCookiesAndConfig({
                    isUserExist,
                    experiments: [data.experiment],
                });
            }
        }
    };

    return {
        subscribe,
        update,
        set,
        loadStories,
        addStories,
        setMute: (isMute) => {
            updateStore({ isMute });
            if (isMute) {
                localStorageService.setItem('storyFeedIsMute', true);
            } else {
                localStorageService.removeItem('storyFeedIsMute');
            }
        },
        setLastWatchedId: (lastWatchedId) => {
            updateStore({ lastWatchedId });
            if (lastWatchedId) {
                localStorageService.setItem('storyFeedLastWatchedId', lastWatchedId);
            } else {
                localStorageService.removeItem('storyFeedLastWatchedId');
            }
        },
        setHideSwipeInfo: (hideSwipeInfo) => {
            updateStore({ hideSwipeInfo });
            if (hideSwipeInfo) {
                localStorageService.setItem('storyFeedHideSwipeInfo', true);
            } else {
                localStorageService.removeItem('storyFeedHideSwipeInfo');
            }
        },
        markStoryWatched: (id) => {
            update((state) => ({
                ...state,
                watchedStories: {
                    ...state.watchedStories,
                    [id]: true,
                },
            }));
        },
        setBlocking: (isBlocked: boolean) => {
            update((state) => {
                if (!isBlocked) {
                    localStorageService.removeItem('storyFeedBlockedDate');
                    return { ...state, blockedDate: null };
                }

                const blockedDate = state.blockedDate ?? Date.now();
                const firstBlockedDate = !state.firstBlockedDate || state.blockingCount === 1
                    ? blockedDate
                    : state.firstBlockedDate;
                const blockingCount = state.blockedDate ? state.blockingCount : state.blockingCount + 1;
                localStorageService.setItem('storyFeedFirstBlockedDate', firstBlockedDate.toString());
                localStorageService.setItem('storyFeedBlockedDate', blockedDate.toString());
                localStorageService.setItem('storyFeedBlockingCount', blockingCount.toString());
                return { ...state, blockedDate, firstBlockedDate, blockingCount };
            });
        },
    };
}

export const storyFeedStore = createStore();

export const isStoryFeedBlocked: Readable<boolean> = derived(
    [storyFeedStore, isCurrentSubscriptionActive],
    ([$store, $isCurrentSubscriptionActive]) => {
        if ($isCurrentSubscriptionActive) {
            return false;
        }

        return Boolean($store.blockedDate && (Date.now() - $store.blockedDate < FEED_BLOCKING_TIME));
    }
);

export const limitedStories: Readable<StoryFeedItem[]> = derived(
    [storyFeedStore, userStore, isCurrentSubscriptionActive],
    ([$store, $userStore, $isCurrentSubscriptionActive]) => {
        if ($isCurrentSubscriptionActive) {
            return $store.stories;
        }

        const blockingCount = $store.blockingCount;
        const userLimit = $userStore.user ? LIMIT_TO_WATCH_AUTHORIZED : LIMIT_TO_WATCH_NON_AUTHORIZED;
        const limitToWatch = blockingCount * userLimit + 1;
        return $store.stories.slice(0, limitToWatch);
    }
);

export const lastWatchedStoryIndex: Readable<number> = derived(
    [storyFeedStore, limitedStories],
    ([$store, $limitedStories]) => {
        if (!$store.lastWatchedId || !$limitedStories.length) {
            return 0;
        }

        const index = $limitedStories.findIndex(({ id }) => id === $store.lastWatchedId);
        if (index === -1) {
            localStorageService.removeItem('storyFeedLastWatchedId');
            return 0;
        }

        return index;
    }
);
